import { router } from "@inertiajs/react"
import { Plus, X } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import InputError from "@/components/input-error"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useOnline } from "@/hooks/use-online"
import { toMessages } from "@/lib/form-errors"
import { cn } from "@/lib/utils"
import { candidatePath } from "@/routes"

/**
 * OKS-18 — Tags editor (UX Spec §Tags section).
 *
 * **Inertia router.patch with optimistic UI** — UX Spec calls for
 * optimistic chip add + rollback-on-failure. Implementation:
 *   - User types in the Input + presses Enter (or clicks Add) → chip
 *     appears locally + Inertia PATCH posts the full tags array
 *     (all-of: existing + new).
 *   - On `onError`: revert to the server-supplied tags, show
 *     `tags_save_failed` sonner toast.
 *   - On `onSuccess`: server-supplied tags re-render via Inertia auto
 *     revalidation; success toast `tags_saved` flows via useFlash from
 *     the controller's `flash[:success]`.
 *
 * **Crew form rules respected:** plain shadcn `Input` + `Button`; no
 * Inertia `<Form>` here because the optimistic-rollback flow needs
 * imperative control of the request lifecycle (Form's render-prop
 * model can't easily serve "patch invisible to dom + revert on error").
 * Still no react-hook-form / zod.
 *
 * **OKS-608 — inline server-error surface.** Because tags uses
 * `router.patch` (not `<Form>`), it had NO inline error region — a
 * rejected save only flashed a transient toast. It now reads the field
 * error off the `onError(errors)` callback (`errors.tags`, normalized via
 * the shared `toMessages`) into local `tagsError` state and renders it in
 * a new inline region below the Add trigger, consistent with the email +
 * notes editors. The transient toast is KEPT as the immediate "couldn't
 * save" signal (UX Spec §4); the inline region carries the specific
 * reason. A rejection with no field-specific message (e.g. a network
 * failure) shows only the toast — the inline region stays empty. The
 * optimistic chip still rolls back on failure. The error clears on the
 * next add/remove interaction.
 *
 * Read-only mode (`!can_edit`): renders chips with no remove
 * affordance + no input.
 *
 * Locked testids:
 *   - `candidate-detail-tags-section` — Card wrapper
 *   - `candidate-detail-tags-form` — form element (around add input)
 *   - `candidate-detail-tags-input` — combobox/text input
 *   - `candidate-detail-tags-submit` — Add / Save button
 *   - `candidate-detail-tag-chip` — each tag chip
 *   - `candidate-detail-tag-remove` — X on each chip
 *   - `candidate-detail-tags-error` — OKS-608 inline server-error region
 *     (polite live region; the Add trigger references it via
 *     `aria-describedby` when an error is present)
 *
 * Sanctioned mid-Phase-3 testid extensions (UX-Lead approved, qa-lead
 * binding):
 *   - `candidate-detail-tags-search` — inner cmdk searchbox (the
 *     `<CommandInput>` itself, distinct from the trigger-button
 *     `…-tags-input`). Playwright types into this; Vitest asserts it
 *     exists when the popover is open. Earlier QA flow keyed off cmdk's
 *     `[cmdk-input]` attribute — first-class testid is the canonical
 *     contract; cmdk attribute remains a fallback if the testid is
 *     ever absent.
 *   - `candidate-detail-tags-add-typed` — CommandEmpty free-text accept
 *     button. Renders ONLY when the typed query doesn't match an
 *     existing brand_tag (and isn't already on the candidate).
 *     **Archaeology note for QA fixture authors:** if a Playwright
 *     spec types a value that's pre-seeded into `brand_tags` for the
 *     fixture's brand, the affordance won't render and the spec
 *     fails on a missing element. The free-text branch is only
 *     reachable for net-new tags. Pin spec inputs to values guaranteed
 *     not in `brand_tags` (e.g. UUID-prefixed test tokens).
 *   - `candidate-detail-tags-suggestion` — each Combobox suggestion
 *     item (one per `brand_tags` value not yet applied).
 */
interface CandidateTagsEditorProps {
  candidateId: number
  tags: string[]
  canEdit: boolean
  /**
   * Brand-scoped tag suggestions sourced from
   * `Candidates::FilterOptionsDigest.tags` server-side. Single source
   * of truth shared with the OKS-17 list filter Combobox so spelling
   * stays consistent across surfaces (UX-Lead F2 lock).
   */
  brandTags: string[]
}

export function CandidateTagsEditor({
  candidateId,
  tags: serverTags,
  canEdit,
  brandTags,
}: CandidateTagsEditorProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [optimistic, setOptimistic] = useState<string[] | null>(null)
  const [pendingInput, setPendingInput] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [popoverOpen, setPopoverOpen] = useState(false)
  // OKS-608 — inline server-error surface. Holds the normalized `errors.tags`
  // message(s) from the last failed `router.patch`; `undefined` when there is
  // no error. Rendered in the `candidate-detail-tags-error` region.
  const [tagsError, setTagsError] = useState<string[] | undefined>(undefined)

  const tags = optimistic ?? serverTags
  const trimmedInput = pendingInput.trim()
  const canAdd =
    canEdit &&
    online &&
    !submitting &&
    trimmedInput !== "" &&
    !tags.includes(trimmedInput)

  // Suggestions = brandTags MINUS already-applied tags. Preserves the
  // brand-scoped vocabulary while preventing the user from re-adding a
  // tag they already have. Free-text typing always works alongside —
  // CommandEmpty surfaces "Add: {{query}}" affordance so net-new tags
  // can be added without leaving the keyboard flow.
  const suggestions = useMemo(
    () => brandTags.filter((tag) => !tags.includes(tag)),
    [brandTags, tags],
  )

  function persist(next: string[]) {
    setSubmitting(true)
    // OKS-608 — clear any prior inline error at the start of a new attempt so
    // a stale message never lingers past the next edit interaction.
    setTagsError(undefined)
    router.patch(
      candidatePath(candidateId),
      { candidate: { tags: next } },
      {
        preserveScroll: true,
        onError: (errors) => {
          setOptimistic(null) // rollback
          // OKS-608 — surface the specific field reason inline (normalized via
          // the shared helper), in addition to the transient toast. Inertia's
          // `onError` supplies `Record<string, string>`; `errors.tags` is a
          // bare string, which `toMessages` lifts to the `string[]` shape
          // `InputError` renders. A rejection with no `tags` message leaves the
          // region empty (toast-only) — e.g. a network failure.
          setTagsError(toMessages(errors.tags))
          toast.error(t("candidates.detail.toasts.tags_save_failed"))
        },
        onSuccess: () => {
          setOptimistic(null) // server tags become source of truth
          setTagsError(undefined)
        },
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function add(rawTag?: string) {
    const tag = (rawTag ?? trimmedInput).trim().toLowerCase()
    if (!canEdit || !online || submitting) return
    if (tag === "" || tags.includes(tag)) return
    const next = [...tags, tag]
    setOptimistic(next)
    setPendingInput("")
    setPopoverOpen(false)
    persist(next)
  }

  function remove(tag: string) {
    if (!canEdit || submitting) return
    const next = tags.filter((t) => t !== tag)
    setOptimistic(next)
    persist(next)
  }

  return (
    <Card data-testid="candidate-detail-tags-section">
      <CardHeader>
        <CardTitle>{t("candidates.detail.tags.section_title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "flex flex-wrap gap-2",
            tags.length === 0 && "text-muted-foreground text-sm",
          )}
        >
          {tags.length === 0 && (
            <span>{t("candidates.detail.tags.empty")}</span>
          )}
          {tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              data-testid="candidate-detail-tag-chip"
              data-tag={tag}
              className={cn("gap-1.5", canEdit && "pr-1")}
            >
              <span>{tag}</span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => remove(tag)}
                  aria-label={t("candidates.detail.tags.remove_aria", { tag })}
                  data-testid="candidate-detail-tag-remove"
                  data-tag={tag}
                  disabled={submitting}
                  className="hover:bg-muted -mr-0.5 rounded-sm p-0.5 disabled:opacity-50"
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              )}
            </Badge>
          ))}
        </div>
        {canEdit && (
          <form
            data-testid="candidate-detail-tags-form"
            onSubmit={(e) => {
              e.preventDefault()
              add()
            }}
            className="mt-4"
          >
            <Label htmlFor="candidate-tags-input" className="sr-only">
              {t("candidates.detail.tags.add_placeholder")}
            </Label>
            {/*
              Combobox typeahead pattern (UX-Lead F2 lock). Uses the
              same shadcn `Command` + `Popover` composition as the
              OKS-17 location filter so the visual + keyboard idiom is
              consistent. Suggestions = brandTags minus applied tags;
              free-text typing falls back to CommandEmpty's "Add:
              {query}" affordance so net-new tags work without a
              second control.
            */}
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="candidate-detail-tags-input"
                    disabled={submitting || !online}
                    // OKS-608 — associate the Add control with the inline
                    // error region only when an error is present (UX Spec §6).
                    aria-describedby={
                      tagsError ? "candidate-tags-error" : undefined
                    }
                    className="w-full justify-start"
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    {t("candidates.detail.tags.add_placeholder")}
                  </Button>
                }
              />
              <PopoverContent className="w-72 p-0" align="start">
                <Command>
                  <CommandInput
                    data-testid="candidate-detail-tags-search"
                    placeholder={t("candidates.detail.tags.add_placeholder")}
                    value={pendingInput}
                    onValueChange={setPendingInput}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {/*
                        Free-text accept — net-new tags via CommandEmpty
                        action. The button is keyboard-reachable inside
                        the cmdk dropdown.
                      */}
                      {trimmedInput !== "" && !tags.includes(trimmedInput) ? (
                        <button
                          type="button"
                          className="hover:bg-accent w-full px-2 py-1.5 text-left text-sm"
                          onClick={() => add()}
                          data-testid="candidate-detail-tags-add-typed"
                        >
                          {t("candidates.detail.tags.add_typed", {
                            query: trimmedInput,
                          })}
                        </button>
                      ) : (
                        <span className="text-muted-foreground px-2 py-1.5 text-sm">
                          {t("candidates.detail.tags.empty_suggestions")}
                        </span>
                      )}
                    </CommandEmpty>
                    {suggestions.length > 0 && (
                      <CommandGroup
                        heading={t("candidates.detail.tags.existing_label")}
                      >
                        {suggestions.map((tag) => (
                          <CommandItem
                            key={tag}
                            value={tag}
                            onSelect={() => add(tag)}
                            data-testid="candidate-detail-tags-suggestion"
                            data-tag={tag}
                          >
                            {tag}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {/*
              Hidden submit so pressing Enter inside CommandInput
              still triggers the add flow when the user has typed
              free-text. The Combobox handles `Enter on highlighted
              item` natively; this covers `Enter on empty list`.
            */}
            <button
              type="submit"
              data-testid="candidate-detail-tags-submit"
              disabled={!canAdd}
              className="sr-only"
            >
              {t("candidates.detail.tags.add_button")}
            </button>
          </form>
        )}
        {/*
         * OKS-608 — inline server-error region. Polite live region so
         * screen-reader users hear a rejection (tags has no `<Form>`
         * render-prop wiring). `InputError` returns null when there is no
         * message, so the region collapses to an empty announced container
         * in the rest state. Kept below the Add control at all breakpoints
         * (UX Spec §5) — full-width block text, does not shift the chip row.
         */}
        {canEdit && (
          <div
            id="candidate-tags-error"
            data-testid="candidate-detail-tags-error"
            aria-live="polite"
            className="mt-2"
          >
            <InputError messages={tagsError} />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
